import { z } from "zod";
import {
  TOV_EVIDENCE_TARGETS,
  type TovEvidenceTargetTable,
} from "../../enums/tov-mapping";

/** Builds a mapper evidence ref schema scoped to one generated definition table. */
const evidenceRef = <T extends TovEvidenceTargetTable>(table: T) =>
  z.object({
    targetField: z
      .enum(TOV_EVIDENCE_TARGETS[table])
      .describe(`Field on ${table} that this evidence supports.`),
    sourceId: z.uuid().describe("Source file id for the cited evidence."),
    chunkId: z
      .uuid()
      .nullable()
      .optional()
      .describe("Embedded chunk id for the cited evidence, when available."),
    parsedPageId: z
      .uuid()
      .nullable()
      .optional()
      .describe("Parsed page id for the cited evidence, when available."),
    locator: z
      .string()
      .min(1)
      .describe("Human-readable page, section, or chunk locator."),
    claim: z
      .string()
      .min(1)
      .describe("Short source-backed claim supporting the target field."),
    relationship: z
      .string()
      .min(1)
      .default("supports")
      .describe("How the evidence relates to the target field."),
    confidence: z
      .number()
      .int()
      .min(0)
      .max(100)
      .nullable()
      .optional()
      .describe("Confidence score from 0 to 100 for the evidence link."),
  });

/** Extends generated mapper row schemas with strict, table-scoped evidence. */
export const generated = <
  Shape extends z.ZodRawShape,
  T extends TovEvidenceTargetTable,
>(
  schema: z.ZodObject<Shape>,
  table: T,
) =>
  schema.extend({
    evidence: z
      .array(evidenceRef(table))
      .default([])
      .describe("Source-backed evidence links for this generated row."),
  });

export const TovGenericEvidenceRefSchema = z.object({
  targetField: z.string().min(1),
  sourceId: z.uuid(),
  chunkId: z.uuid().nullable().optional(),
  parsedPageId: z.uuid().nullable().optional(),
  locator: z.string().min(1),
  claim: z.string().min(1),
  relationship: z.string().min(1).default("supports"),
  confidence: z.number().int().min(0).max(100).nullable().optional(),
});

export type TovEvidenceRef = z.infer<typeof TovGenericEvidenceRefSchema>;
